import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

interface JunoFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const defaultService = () => ({
  appointment_type: '',
  template_codes: [],
  status: ''
});

const JunoForm = ({ namePrefix, control, disabled, configFields, spacing, submitForm, getValues }: JunoFormProps) => {
  const providersField = configFields?.find((f: any) => f.key === 'providers');
  const otherFields = configFields?.filter((f: any) => f.key !== 'providers');

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);
    if (!existing || existing.length === 0) {
      append({
        provider_name: '',
        accept_new_patient: false,
        services: [defaultService()]
      });
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box>
        <Typography fontSize={16} fontWeight={600} my={2}>
          Provider Details
        </Typography>

        {providers.map((provider: any, index: number) => (
          <ProviderItem
            key={provider.id}
            index={index}
            control={control}
            remove={remove}
            disabled={disabled}
            namePrefix={namePrefix}
            providersFields={providersField}
            totalProviders={providers.length}
          />
        ))}

        <Box display='flex' justifyContent='center'>
          <Button
            variant='contained'
            disabled={disabled}
            onClick={() =>
              append({
                provider_name: '',
                accept_new_patient: false,
                services: [defaultService()]
              })
            }
          >
            + Add Another Provider
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default JunoForm;

const ProviderItem = ({ index, control, remove, disabled, namePrefix, totalProviders, providersFields }: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;
  const providerField = providersFields?.group?.find((f: any) => f.key === 'provider_name');
  const appointmentTypeField = providersFields?.group?.find((f: any) => f.key === 'appointment_type');
  const templateCodesField = providersFields?.group?.find((f: any) => f.key === 'template_codes');
  const statusField = providersFields?.group?.find((f: any) => f.key === 'status');
  const providerOptions = providerField?.options || [];
  const appointmentTypeOptions = appointmentTypeField?.options || [];
  const templateCodeOptions = templateCodesField?.options || [];
  const statusOptions = statusField?.options || [];
  const allProviders =
    useWatch({
      control,
      name: `${namePrefix}.providers`
    }) || [];

  const {
    fields: appointmentTypes,
    append: addAppointmentType,
    remove: removeAppointmentType
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });

  const services =
    useWatch({
      control,
      name: `${providerPrefix}.services`
    }) || [];

  const selectedProviderValues = allProviders
    .map((provider: any, providerIndex: number) => (providerIndex === index ? null : provider?.provider_name))
    .filter(Boolean)
    .map((value: any) => String(value));
  const currentProviderValue = allProviders?.[index]?.provider_name;
  const filteredProviderOptions = providerOptions.filter((option: any) => {
    const optionValue = String(option?.value);
    return optionValue === String(currentProviderValue || '') || !selectedProviderValues.includes(optionValue);
  });

  useEffect(() => {
    if (appointmentTypes.length === 0) {
      addAppointmentType(defaultService());
    }
  }, [appointmentTypes.length, addAppointmentType]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='4px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={5} alignItems='center'>
        <Grid size={9}>
          <Select
            label='Select Provider'
            name={`${providerPrefix}.provider_name`}
            control={control}
            disabled={disabled}
            options={filteredProviderOptions}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accept New Patients'
              />
            )}
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={2}>
          <Typography fontWeight={500}>Appointment Types</Typography>
          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() => addAppointmentType(defaultService())}
          >
            Add Appointment Types
          </Button>
        </Box>

        {appointmentTypes.map((appointmentType: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;
          const currentAppointmentTypeValue = services?.[sIndex]?.appointment_type;
          const selectedAppointmentTypeValues = services
            .map((service: any, serviceIndex: number) => (serviceIndex === sIndex ? null : service?.appointment_type))
            .filter(Boolean)
            .map((value: any) => String(value));
          const filteredAppointmentTypeOptions = appointmentTypeOptions.filter((option: any) => {
            const optionValue = String(option?.value);
            return (
              optionValue === String(currentAppointmentTypeValue || '') ||
              !selectedAppointmentTypeValues.includes(optionValue)
            );
          });

          return (
            <Box key={appointmentType.id} mb={2} p={2} border='1px solid #E5E7EB' borderRadius='8px'>
              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography>Appointment Type - {sIndex + 1}</Typography>
                {appointmentTypes.length > 1 && !disabled && (
                  <IconButton onClick={() => removeAppointmentType(sIndex)}>
                    <RiDeleteBin6Line color='#DA0808' />
                  </IconButton>
                )}
              </Stack>
              <Box display='flex' gap={2} alignItems='center'>
                <Select
                  label={appointmentTypeField?.label || 'Select Type'}
                  name={`${servicePrefix}.appointment_type`}
                  control={control}
                  disabled={disabled}
                  options={filteredAppointmentTypeOptions}
                  fullWidth
                />
              </Box>

              <Box mt={1} display='flex' gap={2}>
                <Select
                  label='Template Codes'
                  name={`${servicePrefix}.template_codes`}
                  control={control}
                  options={templateCodeOptions}
                  multiple
                  disabled={disabled}
                  fullWidth
                />
                <Select
                  label={statusField?.label || 'Status'}
                  name={`${servicePrefix}.status`}
                  control={control}
                  options={statusOptions}
                  disabled={disabled}
                  fullWidth
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
